import { useState } from "react";
import {
  useCompleteTask,
  useThisWeeksTasks,
} from "@/hooks/useThisWeeksTasks";
import type { TaskItem } from "@/lib/api/types";

function TaskRow({
  task,
  onComplete,
}: {
  task: TaskItem;
  onComplete: (id: string) => void;
}) {
  const [pressed, setPressed] = useState(false);
  const meta = [task.crop, task.bedName].filter(Boolean).join(" • ");
  return (
    <button
      type="button"
      data-testid="task-row"
      data-task-id={task.id}
      className="flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition-transform"
      style={{
        background: "transparent",
        minHeight: "56px",
        transform: pressed ? "scale(0.98)" : "scale(1)",
      }}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onClick={() => onComplete(task.id)}
    >
      <span
        aria-hidden
        className="mt-0.5 inline-block h-6 w-6 shrink-0 rounded border-2"
        style={{ borderColor: "#9A8A78" }}
      />
      <span className="flex-1">
        <span
          className="block text-sm font-medium"
          style={{ color: "#1A1620" }}
        >
          {task.title}
        </span>
        {meta && (
          <span
            className="mt-0.5 block text-xs"
            style={{ color: "#9A8A78" }}
          >
            {meta}
          </span>
        )}
      </span>
    </button>
  );
}

export function ThisWeeksTasksCard() {
  const { data, isLoading } = useThisWeeksTasks();
  const completeTask = useCompleteTask();

  return (
    <section
      data-testid="tasks-card"
      className="rounded-2xl p-4"
      style={{ background: "#FFFFFF", border: "1px solid #EAE4DA" }}
    >
      <div className="mb-2 flex items-center justify-between">
        <h2
          className="text-base font-semibold"
          style={{ color: "#1A1620" }}
        >
          This Week
        </h2>
        {data && data.tasks.length > 0 && (
          <span className="text-xs" style={{ color: "#9A8A78" }}>
            {data.tasks.length} {data.tasks.length === 1 ? "task" : "tasks"}
          </span>
        )}
      </div>

      {isLoading && (
        <div data-testid="tasks-loading" className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-xl"
              style={{ background: "#EAE4DA" }}
            />
          ))}
        </div>
      )}

      {!isLoading && data && data.tasks.length === 0 && (
        <p
          data-testid="tasks-empty"
          className="py-6 text-center text-sm"
          style={{ color: "#9A8A78" }}
        >
          All caught up. Nothing planned this week.
        </p>
      )}

      {!isLoading && data && data.tasks.length > 0 && (
        <ul className="divide-y" style={{ borderColor: "#EAE4DA" }}>
          {data.tasks.map((task) => (
            <li key={task.id}>
              <TaskRow
                task={task}
                onComplete={(id) => completeTask.mutate(id)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
